//system
import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@angular-slider/ngx-slider';

//services
import { LogService } from 'src/app/services/common/log.service';
import { UserService } from 'src/app/services/user.service';
import { AlertService } from 'src/app/services/alert.service';
import { FacilityService } from 'src/app/services/facility.service';
import { MetaService } from 'src/app/services/meta.service';

//components
import { AuthBaseComponent } from 'src/app/components/auth-base/auth-base.component';

//interfaces
import { InstructorDetails } from 'src/app/models/meta';
import {
  CLASS_FILTER_DEFAULT_END_TIME,
  CLASS_FILTER_DEFAULT_START_TIME,
  createDefaultFilterModel,
  FilterModel,
} from 'src/app/models/studio/classes';
import {
  PartnerLocationOption,
  addressesForSelectedSchedule,
  formatScheduleSelection,
  instructorsForScheduleSelection,
  parseScheduleSelection,
  selectionIncludesHost,
  studiosForScheduleSelection,
  toggleHostInSelection,
  togglePartnerInSelection,
} from 'src/app/models/studio/shared-schedule';
import { Studio } from 'src/app/models/studio/session-details';
import { Address, Location } from 'src/app/models/common-models';

@Component({
  selector: 'df-session-filter',
  templateUrl: './session-filter.component.html',
  styleUrls: ['./session-filter.component.scss']
})
export class SessionFilterComponent extends AuthBaseComponent implements OnInit, OnDestroy {

  private readonly _unsubscribe: Subject<any> = new Subject();
  private readonly defaultInstructorImage = 'assets/images/No-Image-Placeholder.png';

  @Input() public filterConfig!: any;
  @Input() public filterModel!: FilterModel;
  @Input() public studios: Studio[] = [];
  @Input() public instructors: InstructorDetails[] = [];
  @Input() public addresses: Address[] = [];
  @Input() public locations: Location[] = [];
  @Input() public partnerLocations: PartnerLocationOption[] = [];
  @Input() public studioByPartner: { [key: string]: Studio } = {};
  @Input() public instructorsByPartner: { [key: string]: InstructorDetails[] } = {};
  @Input() public hostScheduleLabel = 'This studio';
  @Output() passEntry: EventEmitter<FilterModel|boolean> = new EventEmitter();

  timingOptions: Options = {
    floor: CLASS_FILTER_DEFAULT_START_TIME,
    ceil: CLASS_FILTER_DEFAULT_END_TIME,
    step: 1,
    enforceRange: true,
    hideLimitLabels: true,
    hidePointerLabels: true,
  };

  submitted = false;
  initialized = false;

  constructor(
    private router: Router,
    private logService: LogService,
    private alertService: AlertService,
    private facilityService: FacilityService,
    private metaService: MetaService,
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private userService: UserService,
  ) {
    super(router, logService, alertService, facilityService);
  }

  override async ngOnInit() {
    await super.ngOnInit();
    if (!this.filterModel) {
      this.filterModel = createDefaultFilterModel();
    }
    this.initialized = true;
  }

  instructorToggle(instructorId: number) {
    const index = this.filterModel.InstructorIds.indexOf(instructorId);
    if (index === -1) {
      this.filterModel.InstructorIds.push(instructorId);
    } else {
      this.filterModel.InstructorIds.splice(index, 1);
    }
  }

  studioToggle(studioId: number) {
    const index = this.filterModel.StudioIds.indexOf(studioId);
    if (index === -1) {
      this.filterModel.StudioIds.push(studioId);
    } else {
      this.filterModel.StudioIds.splice(index, 1);
    }
  }

  addressToggle(addressId: number) {
    const index = this.filterModel.AddressIds.indexOf(addressId);
    if (index === -1) {
      this.filterModel.AddressIds.push(addressId);
    } else {
      this.filterModel.AddressIds.splice(index, 1);
    }
  }

  displayStudios(): Studio[] {
    return studiosForScheduleSelection(this.studios, this.studioByPartner, this.filterModel?.PartnerKey);
  }

  displayInstructors(): InstructorDetails[] {
    return instructorsForScheduleSelection(this.instructors, this.instructorsByPartner, this.filterModel?.PartnerKey);
  }

  displayAddresses(): Address[] {
    return addressesForSelectedSchedule(this.addresses, this.filterModel?.PartnerKey);
  }

  isHostScheduleSelected(): boolean {
    return selectionIncludesHost(this.filterModel?.PartnerKey);
  }

  resetScheduleToHost(): void {
    this.filterModel.PartnerKey = '';
  }

  selectHostSchedule(): void {
    this.filterModel.PartnerKey = formatScheduleSelection(
      toggleHostInSelection(parseScheduleSelection(this.filterModel.PartnerKey))
    );
  }

  selectPartnerLocation(key: string): void {
    this.filterModel.PartnerKey = formatScheduleSelection(
      togglePartnerInSelection(parseScheduleSelection(this.filterModel.PartnerKey), key)
    );
  }

  isPartnerLocationSelected(key: string): boolean {
    return parseScheduleSelection(this.filterModel?.PartnerKey).partnerKeys.includes(key);
  }

  locationToggle(locationId: number) {
    if (this.filterModel.LocationId === locationId) {
      this.filterModel.LocationId = 0;
    } else {
      this.filterModel.LocationId = locationId;
    }
  }

  onTimingSliderChange() {
    if (this.filterModel.EndTime - this.filterModel.StartTime < 1) {
      this.filterModel.EndTime = Math.min(
        this.filterModel.StartTime + 1,
        CLASS_FILTER_DEFAULT_END_TIME
      );
    }
  }

  formatTime(hour: number): string {
    const period = hour >= 12 ? 'PM' : 'AM';
    let displayHour = hour > 12 ? hour - 12 : hour;
    if (displayHour === 0) {
      displayHour = 12;
    }
    return `${displayHour} ${period}`;
  }

  isInstructorSelected(instructorId: number): boolean {
    return this.filterModel.InstructorIds.includes(instructorId);
  }

  isStudioSelected(studioId: number): boolean {
    return this.filterModel.StudioIds.includes(studioId);
  }

  isAddressSelected(addressId: number): boolean {
    return this.filterModel.AddressIds.includes(addressId);
  }

  isDefaultTimeRange(): boolean {
    return (
      this.filterModel.StartTime === CLASS_FILTER_DEFAULT_START_TIME &&
      this.filterModel.EndTime === CLASS_FILTER_DEFAULT_END_TIME
    );
  }

  getInstructorImageUrl(instructor: InstructorDetails): string {
    return instructor.Avatar?.UploadedUrl || this.defaultInstructorImage;
  }

  clearInstructors(): void {
    this.filterModel.InstructorIds = [];
  }

  clearStudios(): void {
    this.filterModel.StudioIds = [];
  }

  clearAddresses(): void {
    this.filterModel.AddressIds = [];
  }

  clearTimeRange(): void {
    this.filterModel.StartTime = CLASS_FILTER_DEFAULT_START_TIME;
    this.filterModel.EndTime = CLASS_FILTER_DEFAULT_END_TIME;
  }

  clearFilter(): void {
    this.filterModel = createDefaultFilterModel();
  }

  filterSessions(): void {
    this.closeModal(this.filterModel);
  }

  closeModal(result: FilterModel|boolean) {
    this.passEntry.emit(result);
    this.activeModal.close(result);
  }

  ngOnDestroy(): void {
    this._unsubscribe.next(null);
    this._unsubscribe.complete();
  }

  hasFilter(filter: string): boolean {
    return this.filterConfig.includes(filter);
  }
}
